<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
const model = defineModel<string>({ required: true })

defineProps<{
  presets: { color: string; label: string }[]
}>()

const pickerRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const showPicker = ref(false)
const isEditing = ref(false)
const inputValue = ref('')

onClickOutside(pickerRef, () => {
  showPicker.value = false
})

const displayValue = computed(() => model.value.toUpperCase())

const startEditing = (event: MouseEvent) => {
  event.stopPropagation()
  isEditing.value = true
  inputValue.value = model.value.toUpperCase()
  nextTick(() => {
    inputRef.value?.focus()
  })
}

const finishEditing = () => {
  isEditing.value = false
  // Clean the input: remove # if present, take only valid hex chars
  let cleaned = inputValue.value
    .replace(/^#/, '')
    .replace(/[^0-9a-fA-F]/g, '')
    .toUpperCase()
  // Ensure it's a valid 6-char hex, otherwise keep previous value
  if (cleaned.length === 6) {
    model.value = cleaned.toLowerCase()
  } else if (cleaned.length === 3) {
    // Expand 3-char hex to 6-char (e.g., FFF -> FFFFFF)
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('')
    model.value = cleaned.toLowerCase()
  }
}

const onInputKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    finishEditing()
  } else if (event.key === 'Escape') {
    isEditing.value = false
  }
}

const togglePicker = () => {
  if (!isEditing.value) {
    showPicker.value = !showPicker.value
  }
}
</script>

<template>
  <div ref="pickerRef" class="relative">
    <div
      class="flex h-9.5 w-full cursor-pointer items-center gap-2.5 rounded-control border border-border px-2.5 transition-all bg-white"
      @click="togglePicker"
    >
      <div class="h-5 w-5 rounded-md ring-1 ring-inset ring-black/10" :style="{ backgroundColor: `#${model}` }" />
      <input
        v-if="isEditing"
        ref="inputRef"
        v-model="inputValue"
        type="text"
        maxlength="7"
        class="flex-1 bg-transparent font-mono text-xs text-foreground-secondary outline-none w-full"
        @blur="finishEditing"
        @keydown="onInputKeydown"
        @click.stop
      />
      <span v-else class="flex-1 font-mono text-xs text-foreground-secondary" @click.stop="startEditing"
        >#{{ displayValue }}</span
      >
      <BaseIcon name="IconoirNavArrowDown" class="mr-0.5 h-4 w-4 text-muted-foreground" />
    </div>
    <div
      v-if="showPicker"
      class="absolute left-0 top-full z-10 mt-2 rounded-xl border border-border bg-white p-3 shadow-md"
    >
      <BaseColorPicker v-model="model" :presets="presets" />
    </div>
  </div>
</template>
